import angular from 'angular';
import * as devices from '../../../../modules/devices';
class VideoProviderFactory {
    constructor(VideoProviderResolver, $timeout, pn, $injector, $ngRedux) {
        this.VideoProviderResolver = VideoProviderResolver;
        this.$timeout = $timeout;
        this.pn = pn;
        this.$injector = $injector;
        this.$ngRedux = $ngRedux;
        this.cache = {};
        // Defer realtime connection setup until first access
        // This prevents Error 80017 during Angular bootstrap
    }
    async getProviderService(name) {
        if (this.cache[name]) {
            return this.cache[name];
        }
        const providerModule = await import(`../../${name}/${name}.module.js`);
        this.$injector.loadNewModules([providerModule.default]);
        const provider = this.VideoProviderResolver.getProviderService(name);
        this.cache[name] = provider;
        return provider;
    }
    async setupActiveProvider(name) {
        const provider = await this.getProviderService(name);
        await this.$timeout(() => {
            this._activeProvider = provider;
            this._activeProviderName = name;
        });
        return provider;
    }
    async switchProvider(name) {
        await this.pn.setState({ vcp: name });
        const provider = await this.setupActiveProvider(name);
        if (devices.selectors.selectMediaAccessability(this.$ngRedux.getState())) {
            await provider.initPublisher();
        }
        return provider;
    }
    get activeProvider() {
        // Initialize provider on first access to prevent premature realtime connections
        if (!this._activeProvider && !this._activeProviderPromise) {
            this._activeProviderPromise = this.initializeProvider();
        }
        return this._activeProvider;
    }
    async initializeProvider() {
        try {
            const name = await this.VideoProviderResolver.waitForProviderName();
            return await this.setupActiveProvider(name);
        }
        catch (error) {
            console.warn('Failed to initialize video provider:', error);
            throw error;
        }
    }
    get activeProviderName() {
        // Initialize provider on first access to prevent premature realtime connections
        if (!this._activeProvider && !this._activeProviderPromise) {
            this._activeProviderPromise = this.initializeProvider();
        }
        return this._activeProviderName;
    }
    async switchTo(name) {
        // Ensure provider is initialized before switching
        if (!this._activeProvider && !this._activeProviderPromise) {
            await this.initializeProvider();
        }
        if (this.activeIs(name)) {
            return this._activeProvider;
        }
        console.info(`Switch video provider: ${this._activeProviderName} => ${name}`);
        this._activeProviderPromise = this.switchProvider(name);
        return this._activeProviderPromise;
    }
    /**
     * This decorates scope with dynamic "activeVideoProvider" & "activeVideoProviderName" props
     * It's value is retrieved through getter so scope never keeps
     * a reference to a particular provider implementation
     * as implementation may change at arbitrary moment
     *
     * Example with $scope:
     * `VideoProviderFactory.decorateScope($scope);`
     *
     * Example in component:
     * `VideoProviderFactory.decorateScope(this);`
     *
     * Then use in template:
     * `$ctrl.activeVideoProvider`
     * `$ctrl.activeVideoProviderName`
     *
     * @param {object} scope
     */
    decorateScope(scope) {
        if (scope.hasOwnProperty('activeVideoProvider')) {
            console.warn(`VideoProviderFactory.decorateScope: activeVideoProvider prop already exists`);
        }
        else {
            Object.defineProperty(scope, 'activeVideoProvider', {
                enumerable: true,
                get: () => {
                    return this._activeProvider;
                },
            });
        }
        if (scope.hasOwnProperty('activeVideoProviderName')) {
            console.warn(`VideoProviderFactory.decorateScope: activeVideoProviderName prop already exists`);
        }
        else {
            Object.defineProperty(scope, 'activeVideoProviderName', {
                enumerable: true,
                get: () => {
                    return this._activeProviderName;
                },
            });
        }
    }
    /**
     * This might be weird and somewhat dumb, but can't come up with a better solution.
     * Initialisation of a video provider is asynchronous by nature as it needs to load
     * some scripts (tokbox) and maybe some other modules on demand depending
     * on a provider being used.
     *
     * Also at this point it depends on beta access which is also loaded asynchronously.
     *
     * The suggested workflow will let it be used in a more synchronous manner which would be
     * more convenient (no need to use promises where they are not really needed)
     *
     * Workflow here would be the following:
     * - wait until waitForActiveProvider() is resolved in ui-router `resolve`
     *   e.g.
     *   ```
     *     VideoProvider: VideoProviderFactory => VideoProviderFactory.waitForActiveProvider()
     *   ```
     * - then use `VideoProviderFactory.activeProvider`
     *
     * @return {Promise<AbstractVideoProvider>}
     */
    async waitForActiveProvider() {
        // Ensure provider is initialized before waiting
        if (!this._activeProvider && !this._activeProviderPromise) {
            this._activeProviderPromise = this.initializeProvider();
        }
        return this._activeProviderPromise;
    }
    /**
     * Check if a provider is active at the moment
     *
     * Example:
     * // True if tokbox is current active provider
     * VideoProviderFactory.activeIs('tokbox')
     *
     * Same as:
     * VideoProviderFactory.activeProviderName === 'tokbox'
     *
     * @param {string} name
     * @return {boolean}
     */
    activeIs(name) {
        return name === this._activeProviderName;
    }
}
export { VideoProviderFactory };
export default angular
    .module('doxy.providers.video.common.services.videoProviderFactory', [])
    .service('VideoProviderFactory', VideoProviderFactory).name;
